export type DeviceFormFactor = 'mobile' | 'tablet' | 'desktop';
export type AutomaticInterpretation = 'rules' | 'browser-light';

export interface InterpretationEnvironment {
  formFactor: DeviceFormFactor;
  webGpu: boolean;
  secureContext: boolean;
  deviceMemoryGb?: number;
}

interface NavigatorHints {
  userAgent?: string;
  maxTouchPoints?: number;
  deviceMemory?: number;
  gpu?: unknown;
  userAgentData?: { mobile?: boolean };
}

interface WindowHints {
  innerWidth?: number;
  isSecureContext?: boolean;
  matchMedia?: (query: string) => { matches: boolean };
}

/**
 * 只判断交互形态，不根据品牌猜性能。iPad 的桌面版 UA 仍会通过
 * 触控点数和屏幕宽度识别为平板，避免自动下载数百 MB 模型。
 */
export function detectInterpretationEnvironment(
  navigatorHints: NavigatorHints = typeof navigator === 'undefined' ? {} : navigator as NavigatorHints,
  windowHints: WindowHints = typeof window === 'undefined' ? {} : window
): InterpretationEnvironment {
  const userAgent = navigatorHints.userAgent ?? '';
  const width = windowHints.innerWidth ?? Number.POSITIVE_INFINITY;
  const coarsePointer = windowHints.matchMedia?.('(pointer: coarse)').matches ?? false;
  const touchPoints = navigatorHints.maxTouchPoints ?? 0;
  const reportsMobile = navigatorHints.userAgentData?.mobile === true;
  const isPhone = reportsMobile || /iPhone|iPod|Windows Phone|Android.+Mobile|Mobile/i.test(userAgent);
  const isTablet = !isPhone && (
    /iPad|Tablet|Android/i.test(userAgent)
    || (touchPoints > 1 && coarsePointer && width <= 1180)
  );

  return {
    formFactor: isPhone ? 'mobile' : isTablet ? 'tablet' : 'desktop',
    webGpu: 'gpu' in navigatorHints,
    secureContext: windowHints.isSecureContext ?? true,
    deviceMemoryGb: navigatorHints.deviceMemory
  };
}

/** 手机和平板永不在自动模式下载模型；电脑也必须先满足运行条件。 */
export function chooseAutomaticInterpretation(environment: InterpretationEnvironment): AutomaticInterpretation {
  if (environment.formFactor !== 'desktop') return 'rules';
  if (!environment.webGpu || !environment.secureContext) return 'rules';
  if (environment.deviceMemoryGb !== undefined && environment.deviceMemoryGb < 4) return 'rules';
  return 'browser-light';
}

export function describeInterpretationEnvironment(environment: InterpretationEnvironment): string {
  const automatic = chooseAutomaticInterpretation(environment);
  if (environment.formFactor === 'mobile') {
    return '当前按手机端运行：自动使用免费规则解读，不下载模型；也可以手动选择 API。';
  }
  if (environment.formFactor === 'tablet') {
    return '当前按平板端运行：自动使用免费规则解读，不占用大量存储；也可以手动选择 API。';
  }
  if (automatic === 'browser-light') {
    return '当前按电脑端运行：检测到 WebGPU，自动模式会使用轻量本地模型；首次使用前会明确提示下载大小。';
  }
  return '当前按电脑网页端运行：本地模型条件不足，自动使用免费规则解读，不会阻断排盘和追问。';
}
